// ============================================================================
// asesorFuentes.js — s189-D · de las FUENTES a las PROPUESTAS
// Ticket: la-pantalla-de-fuentes-antes-de-generar
// ============================================================================
//
// QUÉ ES: la coordinación entre lo que el comercio nos dice de su negocio y la
// llamada al asesor. Recibe la fuente de precios elegida y las respuestas, decide
// QUÉ carril usar, arma el contexto y devuelve las propuestas.
//
// POR QUÉ VIVE FUERA DE App.jsx: porque lo van a usar dos pantallas. Hoy la de
// fuentes (post-alta); mañana, cuando converjan, también los dos carriles del alta
// de oferta — que hacen exactamente esto mismo, inline, dentro de un componente de
// mil quinientas líneas (`generateFromUrl` / `generateFromQuestions`).
//
// ⚠️ HAY DUPLICACIÓN Y ES A PROPÓSITO, POR AHORA. Este módulo nace usado por UNA
// sola pantalla. Converger el alta de oferta significaba tocar el componente que se
// acababa de rediseñar (s189-C) dos días antes de una demo con un comercio real, y
// eso no se hace. Lo que se duplica son ~40 líneas de coordinación: las llamadas de
// `api.js` NO se duplican, son las mismas. Ticket para cerrarlo:
// `los-dos-carriles-del-asesor-duplicados`. Cuando se haga, es BORRAR del alta y
// apuntar acá, no reescribir.

import { ingestOfferIdeas, getOfferAdvice } from "./api";

// Las cinco respuestas posibles a «¿dónde están tus precios?». `ninguna` no es un
// error ni un callejón: es una respuesta legítima que manda por el carril de las
// preguntas, que es el que existe justamente para el comercio sin nada publicado.
export const FUENTES = ["mismo_sitio", "otra_pagina", "archivo", "pegado", "ninguna"];

/**
 * Arma el texto que va a leer el asesor.
 *
 * NO es un formato nuevo: es el mismo `user_context` de siempre, con más adentro.
 * El asesor lo recibe como texto y el system prompt ya sabe leerlo; por eso esta
 * pantalla entera no necesita UN SOLO cambio en Bubble.
 *
 * Las etiquetas van en MAYÚSCULA y en español porque así están las que ya funcionan
 * (`CARTA / LISTA DE PRECIOS:` en el carril del menú, `Objetivo:` en el wizard).
 */
export function construirContexto({
  diasValidos = [],      // ["Lun","Mar"] · vacío = todos los días
  franja = "",           // manana | tarde | noche | "" (cualquiera)
  objetivo = "",         // el chip elegido, ya en palabras
  extra = "",            // texto libre: lo que el comercio quiera agregar
  masVende = "",         // solo cuando no hay precios
  precioTipico = "",     // ídem — el número que reemplaza a preguntarle si es caro
  textoPrecios = "",     // la carta pegada o el PDF ya leído
  lang = "es",
} = {}) {
  const en = lang === "en";
  const L = [];

  // Los precios primero: es lo más largo y lo que ancla todo lo demás.
  if (textoPrecios) {
    L.push((en ? "MENU / PRICE LIST:" : "CARTA / LISTA DE PRECIOS:") + "\n" + textoPrecios);
  }

  // El dato que ninguna página web tiene.
  if (diasValidos.length) {
    L.push((en ? "Days the offer should run: " : "Días en que quiere que valgan las ofertas: ") + diasValidos.join(", "));
  } else {
    L.push(en ? "Days the offer should run: any day" : "Días en que quiere que valgan las ofertas: cualquier día");
  }
  const franjas = {
    manana: en ? "morning" : "mañana",
    tarde: en ? "afternoon" : "tarde",
    noche: en ? "evening" : "noche",
  };
  if (franjas[franja]) L.push((en ? "Time of day: " : "Franja horaria: ") + franjas[franja]);

  if (objetivo) L.push((en ? "Goal: " : "Objetivo: ") + objetivo);
  if (masVende) L.push((en ? "Best-seller: " : "Lo que más vende: ") + masVende);
  if (precioTipico) L.push((en ? "Price of the most requested service: $" : "Precio del servicio más pedido: $") + precioTipico);

  // 🔴 Esto va ÚLTIMO y con una orden, no como un dato más. Es lo único del contexto
  // que el asesor tiene que OBEDECER en vez de considerar.
  //
  // El campo es ABIERTO a propósito (s189-D bis): antes preguntaba «¿qué NO quieres
  // descontar?» y esa pregunta presuponía la forma de la respuesta. Un comercio tiene
  // más para decir —«en diciembre me lleno solo», «me sobra cupo en pedicura»— y todo
  // eso cambia las ofertas. Por eso la instrucción cubre los dos casos: respetar lo que
  // escribió, y tratarlo como prohibición cuando lo sea. Proponer un descuento sobre lo
  // que el comercio dijo que no toca es el fallo más irritante que puede tener esto.
  if (extra) {
    L.push((en
      ? "WHAT THE MERCHANT ADDED — respect it. If they ask you not to discount something, do not propose it: "
      : "LO QUE EL COMERCIO AGREGÓ — respétalo. Si pide no descontar algo, no lo propongas: ") + extra);
  }

  return L.join("\n");
}

/**
 * Pide las propuestas. Elige el carril por la fuente:
 *
 *   sitio (mismo u otro) → `ingestOfferIdeas`  (Firecrawl lee la página)
 *   todo lo demás        → `getOfferAdvice`    (el texto que ya tenemos en la mano)
 *
 * NUNCA lanza un error sin código: el que sube trae `e.code` para que la pantalla
 * lo traduzca con `advisorErrorMessage` y le diga al comercio qué hacer. Un error
 * sin código termina en «(código: EMPTY)» en la cara del comercio — pasó, es el
 * s176 FIX 6, y no vuelve a pasar.
 */
// s244 · `perfil` (el de get_merchant_profile que la pantalla ya tiene) lleva al asesor el texto del negocio y el horario
// (api.js bloquesDelPerfil). Sin él, api.js lo pide una vez.
// s244-D7 · `tamanos` (App.jsx tamanosDelAsesor): los números de las cuatro tarjetas, que decide React y no la IA.
export async function pedirPropuestas({ fuente, url = "", textoPrecios = "", rubro = "", contexto = "", lang = "es", perfil, tamanos }) {
  const porSitio = (fuente === "mismo_sitio" || fuente === "otra_pagina") && url.trim();

  const r = porSitio
    ? await ingestOfferIdeas({ business_type: rubro, url: url.trim(), lang, perfil, tamanos })
    : await getOfferAdvice({ business_type: rubro, user_context: contexto, lang, perfil, tamanos });

  if (!r || !r.offers || r.offers.length === 0) {
    const vacio = new Error("ADVISOR_EMPTY");
    vacio.code = "ADVISOR_EMPTY";
    throw vacio;
  }
  return r;
}
